import {
  Alert,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  IconDownloadLine,
  IconWarningLine,
  Pill,
  TextInput,
  View,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { get, set } from "idb-keyval";
import Papa from "papaparse";
import { useEffect, useRef, useState } from "react";
import { useLocation, useOutletContext } from "react-router";
import { Card } from "../../../../../components/Card";
import {
  ERROR,
  useNotification,
} from "../../../../../components/CustomHooks/NotificationProvider";
import EnrichedFileDrop from "../../../../../components/LearningOutcomes/FileDrop/EnrichedFileDrop";
import {
  isArchived,
  sortOutcomes,
} from "../../../../../components/LearningOutcomes/utils";
import RatingScaleSelect from "../../../../../components/Selectors/RatingScaleSelect";
import { UserService } from "../../../../../services/UserService";
import { downloadCSVTemplate } from "../../../../../utils/DownloadCSVTemplate";
import { fileReader } from "../../../../../utils/FileUtils";
import RatingScalePreview from "../RatingScalePreview";

const UploadScores = () => {
  const { t } = useTranslation();
  const { setNextEnabled, setFormData } = useOutletContext();
  const showNotification = useNotification();
  const location = useLocation();
  const section = location.state.section;

  const [file, setFile] = useState(null);
  const [assignmentName, setAssignmentName] = useState("");
  const [selectedOutcomes, setSelectedOutcomes] = useState([]);
  const [ratingScaleFilter, setRatingScaleFilter] = useState([]);
  const [selectedMapping, setSelectedMapping] = useState("");
  const [clear, setClear] = useState(false);
  const [students, setStudents] = useState(null);

  const outcomes = sortOutcomes(
    section?.learning_outcomes?.filter((outcome) => !isArchived(outcome))
  );
  const bottomRef = useRef(null);

  useEffect(() => {
    get("uploadFile").then((file) => {
      if (file) {
        setFile(file);
      }
    });
  }, []);

  useEffect(() => {
    if (selectedMapping && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedMapping]);

  useEffect(() => {
    UserService.getRoster(section.id).then((data) => {
      setStudents(data);
    });
  }, [section.id]);

  function handleOutcomeSelection(selectedOutcomes) {
    setSelectedOutcomes(selectedOutcomes);
    // find common rating scales by checking each selected outcomes rating scales list to find common rating_to_mastery_level_map values
    let commonScales = [];
    selectedOutcomes.forEach((outcomeId, index) => {
      const outcome = outcomes.find((o) => o.id === outcomeId);
      const outcomeScales = outcome.rating_scales.map(
        (rs) => rs.rating_to_mastery_level_map
      );
      if (index === 0) {
        commonScales = outcomeScales;
      } else {
        commonScales = commonScales.filter((scale) =>
          outcomeScales.includes(scale)
        );
      }
    });
    setRatingScaleFilter(commonScales);
    if (!commonScales.includes(selectedMapping)) {
      setClear(true);
      setSelectedMapping("");
    }
  }

  function handleDownload() {
    try {
      const headers = [
        "outcome_id",
        "outcome_name",
        "outcome_statement",
        "student",
        "student_email",
        "canvas_user_id",
        "mastery_at",
        "score",
        "possible",
        "assignment_name",
        "canvas_learning_outcome_id",
        "mastery_level_map_id",
        "canvas_course_id",
      ];

      const resultsData = [];
      const ratingScale = outcomes
        .find((o) => o.id === selectedOutcomes[0])
        .rating_scales.find(
          (rs) => rs.rating_to_mastery_level_map === selectedMapping
        );
      // find rating level in ratingScale.rating_levels with highest points field
      const possible = ratingScale
        ? ratingScale.rating_levels.reduce(
            (max, level) => Math.max(max, level.points),
            0
          )
        : 0;
      const masteryAt = ratingScale ? ratingScale.mastery_at : "";
      students.forEach((user) => {
        outcomes.forEach((outcome) => {
          if (selectedOutcomes.includes(outcome.id)) {
            resultsData.push({
              outcome_id: outcome.id,
              outcome_name: outcome.name,
              outcome_statement: outcome.statement,
              student: user.name,
              student_email: user.email,
              canvas_user_id: user.canvas_id,
              mastery_at: masteryAt,
              score: "",
              possible: possible,
              assignment_name: assignmentName,
              canvas_learning_outcome_id: outcome.canvas_outcome_id
                ? outcome.canvas_outcome_id
                : -1,
              mastery_level_map_id: selectedMapping,
              canvas_course_id: section.canvas_id,
            });
          }
        });
      });

      downloadCSVTemplate("scores_template.csv", headers, resultsData);
    } catch {
      showNotification(t("notifications.general.downloadError"), ERROR);
    }
  }

  async function validateResultsFile(files) {
    try {
      const resultsFile = files[0];
      const fr = await fileReader(resultsFile);
      const content = fr.result;
      const parsed = Papa.parse(content, { skipEmptyLines: true });

      const rows = parsed.data;
      const headers = rows[0];
      const dataRows = rows.slice(1);

      const headerIndex = {};
      headers.forEach((h, idx) => {
        headerIndex[h] = idx;
      });
      for (let i = 0; i < dataRows.length; i++) {
        const columns = dataRows[i];
        const score = columns[headerIndex["score"]];
        const ratingMapId =
          // handles template download and file upload in different sessions
          columns[headerIndex["mastery_level_map_id"]].split("=")[0];
        let ratingScale = {};
        outcomes.forEach((outcome) => {
          outcome.rating_scales.forEach((rs) => {
            if (rs.rating_to_mastery_level_map.split("=")[0] === ratingMapId) {
              ratingScale = rs;
            }
          });
        });
        if (Object.keys(ratingScale).length === 0) {
          showNotification(
            t("results.errorInvalidRatingMap", { rowObject: `row: ${i + 1}` }),
            ERROR
          );
          return;
        }
        const validScore = ratingScale.rating_levels.some(
          (level) => level.points.toString() === score
        );
        if (!validScore) {
          showNotification(
            t("results.errorInvalidScore", { rowNum: i + 1, score: score }),
            ERROR
          );
          return;
        }
      }
      setNextEnabled(true);
      setFormData((prev) => ({
        ...prev,
        metadata: { assignmentName: resultsFile.name },
      }));
      await set("uploadFile", resultsFile);
    } catch {
      showNotification(t("results.parsingError"), ERROR);
    }
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        height: "100%",
      }}
    >
      <Heading border="bottom" level="h3" as="h2">
        {t("finalizeCourse.uploadScores")}
      </Heading>
      {outcomes.length > 0 ? (
        <div
          style={{
            flex: "1",
            overflowY: "auto",
            margin: "-0.75rem",
            padding: "0.75rem",
          }}
        >
          <Flex gap="small" wrap="wrap">
            <Flex.Item size="100%">
              <EnrichedFileDrop
                fileState={file}
                setFileState={setFile}
                fileDropHeading={t("results.dropFileHeading")}
                fileExtension=".csv"
                onFileDrop={validateResultsFile}
              />
            </Flex.Item>
            <Flex.Item shouldGrow>
              <Card>
                <CheckboxGroup
                  name="includeOutcomes"
                  description={t("dashboard.includeOutcomes")}
                  size="small"
                  value={selectedOutcomes}
                  onChange={handleOutcomeSelection}
                >
                  {outcomes.map((outcome) => (
                    <Checkbox
                      key={outcome.id}
                      label={outcome.name + ": " + outcome.statement}
                      value={outcome.id}
                    />
                  ))}
                </CheckboxGroup>
              </Card>
            </Flex.Item>
            <Flex.Item size="100%">
              <View as="div" borderWidth="small 0 0 0" />
            </Flex.Item>
            <Flex.Item size="100%">
              <TextInput
                renderLabel={t("results.assignmentName")}
                value={assignmentName}
                onChange={(e) => setAssignmentName(e.target.value)}
              />
            </Flex.Item>
            <Flex.Item size="100%">
              <RatingScaleSelect
                mapIdsFilter={ratingScaleFilter}
                selectHandler={(rs) => {
                  setSelectedMapping(rs);
                }}
                masteryMapping={selectedMapping}
                title={t("results.selectRatingScale")}
                clear={clear}
                interaction={selectedOutcomes.length > 0}
              />
            </Flex.Item>
            <Flex.Item shouldGrow textAlign="end">
              {students?.length === 0 && (
                <Pill
                  color="warning"
                  renderIcon={<IconWarningLine />}
                  margin="0 small 0 0"
                >
                  {t("finalizeCourse.noStudentsEnrolled")}
                </Pill>
              )}
              <Button
                color="primary"
                renderIcon={IconDownloadLine}
                onClick={handleDownload}
                disabled={
                  outcomes.length === 0 ||
                  selectedOutcomes.length === 0 ||
                  !selectedMapping ||
                  !assignmentName
                }
              >
                {t("results.downloadTemplate")}
              </Button>
            </Flex.Item>
            <Flex.Item size="100%">
              <div ref={bottomRef}>
                {selectedMapping && (
                  <RatingScalePreview ratingScaleId={selectedMapping} />
                )}
              </div>
            </Flex.Item>
          </Flex>
        </div>
      ) : (
        <Alert variant="warning">
          {t("finalizeCourse.noOutcomesAvailable")}
        </Alert>
      )}
    </div>
  );
};

export default UploadScores;
